import { Component, Inject, OnInit, forwardRef } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DictionaryItem } from '../../../interfaces/dictionary-item.interface';
import { PredefinedValue } from '../../../interfaces/predefined-value.interface';
import { isReferenceField, ReferenceField } from '../../../interfaces/reference-field.interface';
import { DataSourceValueField } from '../data-source/data-source-field';
import { DataSourceOption, DataSourceOptions } from '../data-source/data-source-options';
import { DataSourceOptionsService } from '../../../services/data-source-options/data-source-options.service';
import { ConfigServiceProvider } from '../../../services/config/config.service';
import { debounceTime } from 'rxjs/operators';
import { SmartSelectOption } from '../../../interfaces/smart-filter-data.interface';

@Component({
  selector: 'forms-dropdown',
  templateUrl: './dropdown.component.html',
    providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent extends DataSourceValueField {
  searchInputControl = new FormControl();
  filterValue: string = '';
  protected isMultiLineLayout: boolean = false;
  protected optionLineHeight: string;

  constructor(
    @Inject(DataSourceOptionsService) private dataSourceOptionsService: DataSourceOptionsService,
    @Inject(ConfigServiceProvider) private configService: ConfigServiceProvider,
  ) {
    super();
    this.initSearchInput();
  }

  private initSearchInput(): void {
    this.searchInputControl.valueChanges.pipe(debounceTime(500)).subscribe((value) => this.setDropdownValues(value));
  }

  private setDropdownValues(value: string = ''): void {
    const searchPhase = value && value.trim();

    if (!searchPhase && this.formField.dataSource !== 'dataView') {
      this.filterValue = searchPhase;
      this.formField.values.forEach((v) => (v.visible = true));
      return;
    }

    if (this.filterValue === searchPhase) {
      return;
    }

    this.setFilterValues(searchPhase);
  }

  private setFilterValues(searchPhase: string): void {
    switch (this.formField.dataSource) {
      case 'dataView':
        this.getDataViewValues(searchPhase);
        break;
      case 'dictionary':
        this.filterValue = searchPhase;
        this.formField.values.forEach((v) => this.markOptionAsVisible(v, 'name', searchPhase));
        break;
      case 'predefined':
        this.filterValue = searchPhase;
        this.formField.values.forEach((v) => this.markOptionAsVisible(v, 'value', searchPhase));
        break;
    }
  }

  private getDataViewValues(searchPhase: string): void {
    this.dataSourceOptionsService.getReferenceValue(this.formField, this.configService.getBackendBaseAPI(), searchPhase).subscribe((values) => {
      this.filterValue = searchPhase;
      this.formField.values = values.map((v) => ({ option: v, visible: true }));

      // https://sellions.atlassian.net/browse/AS-5176
      // Workaround if additional API call after closing selection; assumes that the current value was provided by a previous API call and is valid
      // so if it doesn't exist in the current dataset, let's add it by hand and make it visible
      if (!searchPhase && isReferenceField(this.value) && this.isValidField() && !this.formField.values.some((i) => i.option.id === this.value.id)) {
        this.formField.values.push({ option: this.value, visible: true });
      }
    });
  }

  protected getOptionLineHeight() {
    if (!this.optionLineHeight && (this.formField.dataSource && this.formField.dataSource === 'dataView') && (this.formField.rowFormat && (this.formField.rowFormat.subtitle))) {
      this.isMultiLineLayout = true;
      this.optionLineHeight = this.isMultiLineLayout ? '5em' : '3em';
    }
    return this.optionLineHeight
  }

  private markOptionAsVisible(smartOption: SmartSelectOption<DataSourceOption>, fieldName: string, value: string): void {
    if (!smartOption.option[fieldName]) {
      return;
    }
    smartOption.visible = smartOption.option[fieldName].toLocaleLowerCase().includes(value);
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_forms/projects/forms/src/lib/components/controls/dropdown/dropdown.component.ts